<template>
  <section id="look-feel" class="page-section look-feel-section selection-section">
    <div class="look-feel-content">
      <div class="section-header">
        <h3>Customise your campaign look</h3>
        <p>Click below to change the campaign page designs</p>
      </div>

      <div class="mobile-appearance-controls">
        <button
          type="button"
          class="mobile-control-button"
          :disabled="!hasTemplateSelection"
          aria-label="Open brand controls"
          @click="openBrandHubModal"
        >
          Your Branding
        </button>
        <button
          type="button"
          class="mobile-control-button"
          :disabled="!hasTemplateSelection"
          aria-label="Open colour and button controls"
          @click="openStyleHubModal"
        >
          Your Colours
        </button>
      </div>

      <div class="phone-preview-shell">
        <div class="phone-preview">
          <div class="phone-speaker"></div>
          <div class="phone-screen" :style="phoneScreenStyle">
            <button
              type="button"
              class="background-hit-target"
              :disabled="!hasTemplateSelection"
              aria-label="Customize registration background"
              @click="openBackgroundActionModal('registration')"
            />

            <button
              type="button"
              :class="['tap-target', 'logo-target', { 'preview-target--configured': hasCustomizedLogo }]"
              :disabled="!hasTemplateSelection"
              aria-label="Customize registration logo"
              @click="openAssetActionModal"
            >
              <svg class="marching-ants marching-ants--logo" viewBox="0 0 220 88" preserveAspectRatio="none" aria-hidden="true">
                <rect x="1" y="1" width="218" height="86" rx="14" ry="14" pathLength="100" />
              </svg>
              <img :src="logoPreviewUrl || defaultLogo" alt="Registration logo preview" class="phone-logo" />
            </button>

            <button
              type="button"
              :class="['tap-target', 'text-target', { 'preview-target--configured': hasCustomizedTextSection }]"
              :disabled="!hasTemplateSelection"
              aria-label="Customize registration colours"
              @click="openColorModal('header', 'registration')"
            >
              <svg class="marching-ants marching-ants--panel" viewBox="0 0 220 132" preserveAspectRatio="none" aria-hidden="true">
                <rect x="1" y="1" width="218" height="130" rx="16" ry="16" pathLength="100" />
              </svg>
              <p class="preview-copy" :style="textStyle">Select your text colour here</p>
              <div class="input-preview" :style="textStyle">you@example.com</div>
            </button>

            <button
              type="button"
              :class="['tap-target', 'button-target', { 'preview-target--configured': hasCustomizedRegistrationButton }]"
              :disabled="!hasTemplateSelection"
              aria-label="Customize registration button"
              @click="openButtonStyleModal('registration')"
            >
              <svg class="marching-ants marching-ants--pill" viewBox="0 0 220 56" preserveAspectRatio="none" aria-hidden="true">
                <rect x="1" y="1" width="218" height="54" rx="27" ry="27" pathLength="100" />
              </svg>
              <span class="preview-submit" :style="activePreviewButtonStyle">
                {{ activeButtonConfig?.label || activeFallbackButtonConfig?.label || 'Style your button' }}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <teleport to="body">
      <div
        v-if="isBrandHubModalOpen"
        class="asset-action-modal-overlay"
        @click.self="closeBrandHubModal"
      >
        <div class="asset-action-modal asset-action-modal--hub" role="dialog" aria-modal="true" aria-label="Brand controls">
          <div class="modal-header">
            <button
              type="button"
              class="modal-close-button"
              @click="closeBrandHubModal"
              aria-label="Close brand controls"
            >
              ×
            </button>
            <h3>Your branding</h3>
          </div>
          <div class="modal-body">
            <div class="hub-block">
              <p class="studio-block-title">Logo</p>
              <p class="hub-block-copy">Decide on the logo you want to appear on all pages</p>
              <div class="modal-actions">
                <button type="button" class="action-button" @click="openLogoActionsFromBrandHub">
                  Manage logo
                </button>
              </div>
            </div>

            <div class="hub-block">
              <p class="studio-block-title">Font</p>
              <p class="hub-block-copy">Pick the Google Font for all campaign typography. Defaults to Montserrat.</p>
              <select
                class="font-select-input"
                :value="props.googleFont || DEFAULT_GOOGLE_FONT"
                @change="onGoogleFontChange"
                aria-label="Campaign font"
              >
                <option v-for="font in POPULAR_GOOGLE_FONTS" :key="font" :value="font">
                  {{ font }}
                </option>
              </select>
            </div>

            <div class="hub-block">
              <p class="studio-block-title">Backgrounds</p>
              <p class="hub-block-copy">Select images and colours for page backgrounds</p>
              <div class="screen-control-list">
                <div
                  v-for="screen in backgroundScreens"
                  :key="screen.key"
                  class="screen-control-card"
                >
                  <div class="screen-control-copy">
                    <span class="screen-control-title">{{ screen.label }}</span>
                    <span class="screen-control-description">
                      {{ screen.backgroundPreviewUrl ? 'Select images and colours for page backgrounds' : 'Select images and colours for page backgrounds' }}
                    </span>
                  </div>
                  <button type="button" class="action-button" @click="openBackgroundActionsFromBrandHub(screen.key)">
                    Edit background
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="isStyleHubModalOpen"
        class="asset-action-modal-overlay"
        @click.self="closeStyleHubModal"
      >
        <div class="asset-action-modal asset-action-modal--hub" role="dialog" aria-modal="true" aria-label="Colour and button controls">
          <div class="modal-header">
            <button
              type="button"
              class="modal-close-button"
              @click="closeStyleHubModal"
              aria-label="Close colour and button controls"
            >
              ×
            </button>
            <h3>Colours / buttons</h3>
          </div>
          <div class="modal-body">
            <div class="hub-block">
              <p class="studio-block-title">Buttons</p>
              <div class="screen-control-list">
                <div
                  v-for="screen in styleScreens"
                  :key="screen.key"
                  class="screen-control-card"
                >
                  <div class="screen-control-copy">
                    <span class="screen-control-title">{{ screen.label }}</span>
                    <span class="screen-control-description">
                      Background, text, and button styling
                    </span>
                  </div>
                  <div class="screen-control-actions">
                    <button type="button" class="action-button" @click="openColorStudioFromHub(screen.key)">
                      Colours
                    </button>
                    <button type="button" class="action-button" @click="openButtonStyleFromHub(screen.key)">
                      Button
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="isAssetActionModalOpen"
        class="asset-action-modal-overlay"
        @click.self="closeAssetActionModal"
      >
        <div class="asset-action-modal asset-action-modal--logo" role="dialog" aria-modal="true" aria-label="Logo actions">
          <div class="modal-header">
            <button
              type="button"
              class="modal-close-button"
              @click="closeAssetActionModal"
              aria-label="Close logo actions"
            >
              ×
            </button>
            <h3>Logo to be used on all pages</h3>
          </div>
          <div class="modal-body modal-body--logo-actions">
            <p>Create or upload the logo that will appear on all pages</p>
            <div class="modal-actions">
              <button type="button" class="action-button" @click="handleAssetAction('upload')">
                Upload your own
              </button>
              <button type="button" class="action-button" @click="handleAssetAction('browse')">
                Browse logos
              </button>
              <button
                v-if="logoPreviewUrl"
                type="button"
                class="action-button action-button--danger"
                @click="handleAssetAction('clear')"
              >
                Remove logo
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="isBackgroundActionModalOpen"
        class="asset-action-modal-overlay"
        @click.self="closeBackgroundActionModal"
      >
        <div class="asset-action-modal asset-action-modal--background" role="dialog" aria-modal="true" :aria-label="`${activeBackgroundScreenLabel} background actions`">
          <div class="modal-header">
            <button
              type="button"
              class="modal-close-button"
              @click="closeBackgroundActionModal"
              :aria-label="`Close ${activeBackgroundScreenLabel} background actions`"
            >
              ×
            </button>
            <h3>{{ activeBackgroundScreen === 'prize' ? 'Edit Prize page' : 'Edit Registration page' }}</h3>
          </div>
          <div class="modal-body modal-body--logo-actions">
            <p>Keep your colours and optionally add a dedicated full-screen background image for this page.</p>
            <div class="modal-actions">
              <button type="button" class="action-button" @click="handleBackgroundAction('colour')">
                Edit colours
              </button>
              <button type="button" class="action-button" @click="handleBackgroundAction('upload')">
                Upload image
              </button>
              <button type="button" class="action-button" @click="handleBackgroundAction('browse')">
                Browse backgrounds
              </button>
              <button
                v-if="activeBackgroundPreviewUrl"
                type="button"
                class="action-button action-button--danger"
                @click="handleBackgroundAction('clear')"
              >
                Remove image
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="isColorModalOpen && selectedColorKey"
        class="asset-action-modal-overlay"
        @click.self="closeColorModal"
      >
        <div
          class="asset-action-modal asset-action-modal--single-color"
          role="dialog"
          aria-modal="true"
          :aria-label="`Set ${colorTargetLabel}`"
        >
          <div class="modal-header">
            <button
              type="button"
              class="modal-close-button"
              @click="closeColorModal"
              :aria-label="`Close ${colorTargetLabel} picker`"
            >
              ×
            </button>
            <h3>
              {{
                activeStyleScreen === 'registration' && selectedColorKey === 'header'
                  ? 'Registration pages text colour'
                  : colorTargetLabel
              }}
            </h3>
          </div>
          <div class="modal-body modal-body--single-color">
            <p class="single-color-description">
              {{
                activeStyleScreen === 'registration' && selectedColorKey === 'header'
                  ? 'Choose the colour of the text on the registration pages'
                  : `Choose a colour for the ${activeStyleScreenLabel.toLowerCase()} screen.`
              }}
            </p>
            <div class="single-color-controls">
              <div class="single-color-wheel-wrap">
                <span class="single-color-wheel-guide" :style="singleColorGuideStyle" aria-hidden="true">
                  tap here to pick a colour
                </span>
                <input
                  id="single-colour-wheel"
                  v-model="workingColor"
                  type="color"
                  class="single-color-wheel"
                  aria-label="Pick a colour"
                />
              </div>
              <input
                id="single-colour-hex-input"
                v-model.trim="workingColor"
                type="text"
                class="color-hex-input color-hex-input--single"
                inputmode="text"
                autocomplete="off"
                spellcheck="false"
                aria-label="Hex colour value"
              />
            </div>
            <div class="color-preset-grid color-preset-grid--single" role="list">
              <button
                v-for="preset in colorPresets"
                :key="`${selectedColorKey}-${preset}`"
                type="button"
                :class="['color-preset', 'color-preset--single', { 'color-preset--active': activeColorPreview === preset }]"
                :style="{ '--preset-color': preset }"
                :aria-label="`Use ${preset}`"
                @click="workingColor = preset"
              />
            </div>
            <div v-if="singleColorContrastWarning" class="contrast-warning">
              <span class="contrast-warning-icon">&#9888;</span>
              <span>{{ singleColorContrastWarning }}</span>
            </div>
            <div class="modal-actions">
              <button type="button" class="action-button" @click="closeColorModal">
                Cancel
              </button>
              <button type="button" class="action-button" @click="saveColorChange">
                Save colour
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="isColorStudioModalOpen"
        class="asset-action-modal-overlay"
        @click.self="closeColorStudioModal"
      >
        <div
          class="asset-action-modal asset-action-modal--studio"
          role="dialog"
          aria-modal="true"
          :aria-label="`${activeStyleModalLabel} colour controls`"
        >
          <div class="modal-header">
            <button
              type="button"
              class="modal-close-button"
              @click="closeColorStudioModal"
              :aria-label="`Close ${activeStyleModalLabel} colour controls`"
            >
              ×
            </button>
            <h3>{{ activeStyleModalLabel }} Colours</h3>
          </div>
          <div class="modal-body">
            <div class="studio-block">
              <p class="studio-block-title">Pre-set colour combinations</p>
              <div class="studio-palette-grid" role="list" :aria-label="`${activeStyleScreenLabel} palettes`">
                <button
                  v-for="palette in palettes"
                  :key="`studio-${activeStyleScreen}-${palette.id}`"
                  type="button"
                  class="palette-option palette-option--studio"
                  :aria-label="`Apply ${palette.name} palette`"
                  @click="applyPaletteFromStudio(palette)"
                >
                  <span class="palette-swatches" aria-hidden="true">
                    <span class="palette-swatch" :style="{ '--palette-color': palette.background }"></span>
                    <span class="palette-swatch" :style="{ '--palette-color': palette.header }"></span>
                    <span class="palette-swatch" :style="{ '--palette-color': palette.button }"></span>
                  </span>
                  <span class="palette-name">{{ palette.name }}</span>
                </button>
              </div>
            </div>

            <div class="studio-block">
              <p class="studio-block-title">Individual Colours</p>
              <div class="studio-color-list">
                <div
                  v-for="field in colorStudioFields"
                  :key="field.key"
                  class="studio-color-item"
                >
                  <div class="studio-color-item-header">
                    <span>{{ field.label }}</span>
                    <span class="studio-color-item-description">{{ field.description }}</span>
                  </div>
                  <div class="color-controls">
                    <input
                      :value="colorStudioDrafts[field.key]"
                      type="color"
                      class="color-picker-input"
                      :aria-label="`Pick ${field.label}`"
                      @input="updateColorStudioDraft(field.key, ($event.target as HTMLInputElement).value)"
                    />
                    <input
                      :value="colorStudioDrafts[field.key]"
                      type="text"
                      class="color-hex-input"
                      inputmode="text"
                      autocomplete="off"
                      :aria-label="`${field.label} hex colour value`"
                      @input="updateColorStudioDraft(field.key, ($event.target as HTMLInputElement).value)"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div v-if="contrastWarnings.length" class="contrast-warning">
              <span class="contrast-warning-icon">&#9888;</span>
              <ul>
                <li v-for="warning in contrastWarnings" :key="warning">{{ warning }}</li>
              </ul>
            </div>

            <div class="modal-actions">
              <button type="button" class="action-button" @click="closeColorStudioModal">
                Cancel
              </button>
              <button type="button" class="action-button" @click="saveColorStudioChanges">
                Save colours
              </button>
            </div>
          </div>
        </div>
      </div>
    </teleport>

    <ButtonStyleModal
      :show="isButtonStyleModalOpen"
      :title="`${activeStyleButtonModalLabel} Buttons`"
      :description="activeStyleScreen === 'registration'
        ? 'Set the button colours'
        : activeStyleScreen === 'prize'
          ? 'Set Set the button colours'
          : `Set the label and finish of the ${activeStyleScreenLabel.toLowerCase()} screen button.`"
      :config="activeButtonConfig"
      :fallback-config="activeFallbackButtonConfig"
      :fallback-background-color="activeBrandColors.secondary"
      :fallback-text-color="activeBrandColors.buttonText"
      :preview-background-color="activeBrandColors.background"
      :preview-background-image-url="activeStyleBackgroundPreviewUrl"
      @close="closeButtonStyleModal"
      @save="saveButtonStyle"
    />
  </section>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch, type StyleValue } from 'vue'
import { useUIStore } from '@/store/ui.store'
import ButtonStyleModal from '@/components/ButtonStyleModal.vue'
import type { BackgroundScreenKey, ScreenAppearanceKey, ScreenBrandColors, ScreenColorKey, StyledButtonConfig } from '@/types/form.types'
import { DEFAULT_BUTTON_CONFIGS } from '@/utils/buttonStyles'
import { isButtonConfigCustomized, isGoogleFontCustomized, isScreenBrandColorsCustomized } from '@/utils/previewCustomization'

type AssetType = 'logo' | 'mapMarker' | 'ar' | 'background'
type EditableColorKey = 'secondary' | 'background' | 'header' | 'buttonText'
type StudioColorKey = EditableColorKey
type TemplateConfigLike = { experienceType?: string | null } | null | undefined

type PaletteOption = {
  id: string
  name: string
  background: string
  header: string
  button: string
  buttonText: string
}

// Top Google Fonts by popularity (descending). Source: Google Fonts directory.
// Default falls back to 'Montserrat' if nothing is selected (see template binding).
const POPULAR_GOOGLE_FONTS = [
  'Roboto',
  'Open Sans',
  'Noto Sans',
  'Montserrat',
  'Lato',
  'Poppins',
  'Inter',
  'Roboto Condensed',
  'Source Sans 3',
  'Oswald',
  'Raleway',
  'Roboto Mono',
  'Nunito',
  'Ubuntu',
  'Merriweather',
  'PT Sans',
  'Playfair Display',
  'Rubik',
  'Work Sans',
  'Mukta',
  'Roboto Slab',
  'PT Serif',
  'Quicksand',
  'Bebas Neue',
  'Inconsolata',
  'Hind',
  'Mulish',
  'Heebo',
  'Anton',
  'Fira Sans',
]
const DEFAULT_GOOGLE_FONT = 'Montserrat'
const BACKGROUND_SCREENS: BackgroundScreenKey[] = ['registration', 'prize']
const STYLE_HUB_SCREENS: ScreenAppearanceKey[] = ['registration', 'prize']
const SCREEN_LABELS: Record<ScreenAppearanceKey, string> = {
  registration: 'Design registration pages here',
  interaction: 'Interaction',
  prize: 'Design prize pages here',
}

const PALETTE_OPTIONS: ReadonlyArray<PaletteOption> = [
  {
    id: 'midnight-mint',
    name: 'Midnight Mint',
    background: '#0F172A',
    header: '#EAF2FF',
    button: '#2DD4BF',
    buttonText: '#042F2E'
  },
  {
    id: 'plum-neon',
    name: 'Plum Neon',
    background: '#3B1E3E',
    header: '#FFF0FA',
    button: '#F472B6',
    buttonText: '#FFFFFF'
  },
  {
    id: 'deep-teal',
    name: 'Deep Teal',
    background: '#0B2E33',
    header: '#E6FFFD',
    button: '#22D3EE',
    buttonText: '#083344'
  },
  {
    id: 'cyber-night',
    name: 'Cyber Night',
    background: '#111827',
    header: '#F9FAFB',
    button: '#A3E635',
    buttonText: '#1A2E05'
  },
  {
    id: 'cobalt-signal',
    name: 'Cobalt Signal',
    background: '#1E3A8A',
    header: '#EEF4FF',
    button: '#FACC15',
    buttonText: '#1E293B'
  },
  {
    id: 'sunset-ember',
    name: 'Sunset Ember',
    background: '#7F1D1D',
    header: '#FFF1EE',
    button: '#FB7185',
    buttonText: '#4C0519'
  },
  {
    id: 'ivory-sky',
    name: 'Ivory Sky',
    background: '#F8FAFC',
    header: '#1E293B',
    button: '#2563EB',
    buttonText: '#FFFFFF'
  },
  {
    id: 'tangerine-ink',
    name: 'Tangerine Ink',
    background: '#FFF7ED',
    header: '#7C2D12',
    button: '#F97316',
    buttonText: '#1C1917'
  },
  {
    id: 'rose-paper',
    name: 'Rose Paper',
    background: '#FFF1F2',
    header: '#831843',
    button: '#EC4899',
    buttonText: '#FFFFFF'
  },
  {
    id: 'mint-wash',
    name: 'Mint Wash',
    background: '#ECFDF5',
    header: '#14532D',
    button: '#16A34A',
    buttonText: '#FFFFFF'
  },
  {
    id: 'lemon-pop',
    name: 'Lemon Pop',
    background: '#FEF9C3',
    header: '#422006',
    button: '#BE185D',
    buttonText: '#FFFFFF'
  }
]

const COLOR_STUDIO_FIELDS: ReadonlyArray<{ key: StudioColorKey; label: string; description: string }> = [
  { key: 'background', label: 'Background', description: 'Full-screen page background' },
  { key: 'header', label: 'Base Text', description: 'All non-button text colour' },
  { key: 'secondary', label: 'Button', description: 'Primary button background' },
  { key: 'buttonText', label: 'Button Text', description: 'Primary button label text' }
]

const props = defineProps<{
  templateConfig?: TemplateConfigLike
  isAssetBrowserVisible?: boolean
  isUploadBrowserVisible?: boolean
  brandColors: ScreenBrandColors
  interactionBrandColors: ScreenBrandColors
  prizeBrandColors: ScreenBrandColors
  googleFont?: string
  logoPreviewUrl?: string | null
  backgroundPreviewUrl?: string | null
  prizeBackgroundPreviewUrl?: string | null
  buttonConfig?: StyledButtonConfig | null
  previewButtonStyle: StyleValue
  interactionButtonConfig?: StyledButtonConfig | null
  interactionPreviewButtonStyle: StyleValue
  prizeButtonConfig?: StyledButtonConfig | null
  prizePreviewButtonStyle: StyleValue
  defaultLogo: string
}>()

const emit = defineEmits<{
  (e: 'open-upload-modal', payload: { type: AssetType; screen?: BackgroundScreenKey }): void
  (e: 'open-asset-modal', payload: { type: AssetType; screen?: BackgroundScreenKey }): void
  (e: 'clear-asset', payload: { type: AssetType; screen?: BackgroundScreenKey }): void
  (e: 'update-brand-color', payload: { screen: ScreenAppearanceKey; key: ScreenColorKey; value: string }): void
  (e: 'update-google-font', font: string): void
  (e: 'update-button-config', payload: { screen: ScreenAppearanceKey; value: StyledButtonConfig }): void
}>()

const uiStore = useUIStore()
const isBrandHubModalOpen = ref(false)
const isStyleHubModalOpen = ref(false)
const isAssetActionModalOpen = ref(false)
const isBackgroundActionModalOpen = ref(false)
const isColorModalOpen = ref(false)
const isColorStudioModalOpen = ref(false)
const isButtonStyleModalOpen = ref(false)
const activeStyleScreen = ref<ScreenAppearanceKey>('registration')
const activeBackgroundScreen = ref<BackgroundScreenKey>('registration')
const selectedColorKey = ref<EditableColorKey | null>(null)
const workingColor = ref('#FFFFFF')
const colorStudioDrafts = ref<Record<StudioColorKey, string>>({
  background: props.brandColors.background,
  header: props.brandColors.header,
  secondary: props.brandColors.secondary,
  buttonText: props.brandColors.buttonText
})

const screenBrandColorMap = computed<Record<ScreenAppearanceKey, ScreenBrandColors>>(() => ({
  registration: props.brandColors,
  interaction: props.interactionBrandColors,
  prize: props.prizeBrandColors,
}))

const screenButtonConfigMap = computed<Record<ScreenAppearanceKey, StyledButtonConfig | null | undefined>>(() => ({
  registration: props.buttonConfig,
  interaction: props.interactionButtonConfig,
  prize: props.prizeButtonConfig,
}))

const screenPreviewButtonStyleMap = computed<Record<ScreenAppearanceKey, StyleValue>>(() => ({
  registration: props.previewButtonStyle,
  interaction: props.interactionPreviewButtonStyle,
  prize: props.prizePreviewButtonStyle,
}))

const hasTemplateSelection = computed(() => !!props.templateConfig?.experienceType)
const hasCustomizedLogo = computed(() => Boolean(props.logoPreviewUrl?.trim()))
const hasCustomizedTextSection = computed(() =>
  Boolean(props.backgroundPreviewUrl) ||
  isScreenBrandColorsCustomized('registration', props.brandColors) ||
  isGoogleFontCustomized(props.googleFont),
)
const hasCustomizedRegistrationButton = computed(() => isButtonConfigCustomized('registration', props.buttonConfig))
const palettes = computed(() => PALETTE_OPTIONS)
const colorStudioFields = computed(() => COLOR_STUDIO_FIELDS)
const activeBrandColors = computed(() => screenBrandColorMap.value[activeStyleScreen.value])
const activeStyleScreenLabel = computed(() => SCREEN_LABELS[activeStyleScreen.value])
const activeStyleModalLabel = computed(() =>
  activeStyleScreen.value === 'registration'
    ? 'Registration Pages'
    : activeStyleScreen.value === 'prize'
      ? 'Prize Pages'
      : activeStyleScreenLabel.value,
)
const activeStyleButtonModalLabel = computed(() =>
  activeStyleScreen.value === 'registration'
    ? 'Registration Pages'
    : activeStyleScreen.value === 'prize'
      ? 'Prize Pages'
      : activeStyleScreenLabel.value,
)
const activeBackgroundScreenLabel = computed(() => SCREEN_LABELS[activeBackgroundScreen.value])
const activeFallbackButtonConfig = computed(() => DEFAULT_BUTTON_CONFIGS[activeStyleScreen.value])
const activeButtonConfig = computed(() => screenButtonConfigMap.value[activeStyleScreen.value] ?? null)
const activePreviewButtonStyle = computed(() => screenPreviewButtonStyleMap.value[activeStyleScreen.value])
const activeStyleBackgroundPreviewUrl = computed(() =>
  activeStyleScreen.value === 'registration'
    ? props.backgroundPreviewUrl || null
    : activeStyleScreen.value === 'prize'
      ? props.prizeBackgroundPreviewUrl || null
      : null,
)
const activeBackgroundPreviewUrl = computed(() =>
  activeBackgroundScreen.value === 'registration'
    ? props.backgroundPreviewUrl || null
    : props.prizeBackgroundPreviewUrl || null,
)
const backgroundScreens = computed(() =>
  BACKGROUND_SCREENS.map((screen) => ({
    key: screen,
    label: SCREEN_LABELS[screen],
    backgroundPreviewUrl: screen === 'registration' ? props.backgroundPreviewUrl || null : props.prizeBackgroundPreviewUrl || null,
  })),
)
const styleScreens = computed(() =>
  STYLE_HUB_SCREENS.map((screen) => ({
    key: screen,
    label: SCREEN_LABELS[screen],
  })),
)
const isAnyModalOpen = computed(() =>
  isBrandHubModalOpen.value ||
  isStyleHubModalOpen.value ||
  isAssetActionModalOpen.value ||
  isBackgroundActionModalOpen.value ||
  isColorModalOpen.value ||
  isColorStudioModalOpen.value ||
  isButtonStyleModalOpen.value
)

const phoneScreenStyle = computed(() => ({
  backgroundColor: props.brandColors.background,
  backgroundImage: props.backgroundPreviewUrl ? `url('${props.backgroundPreviewUrl}')` : undefined,
  backgroundSize: props.backgroundPreviewUrl ? 'cover' : undefined,
  backgroundPosition: props.backgroundPreviewUrl ? 'center' : undefined,
  backgroundRepeat: props.backgroundPreviewUrl ? 'no-repeat' : undefined,
  fontFamily: props.googleFont || 'Montserrat'
}))

const textStyle = computed(() => ({
  color: props.brandColors.header
}))

const colorTargetLabel = computed(() => {
  if (selectedColorKey.value === 'background') {
    return 'Background Colour'
  }
  if (selectedColorKey.value === 'secondary') {
    return 'Button Colour'
  }
  if (selectedColorKey.value === 'buttonText') {
    return 'Button Text Colour'
  }
  return 'Text Colour'
})

const colorPresets = computed(() => {
  const values = [
    '#7AE6B1',
    '#96FAC9',
    '#121936',
    '#292E50',
    '#FFFFFF'
  ]

  return Array.from(new Set(values.map((color) => color.toUpperCase())))
})

const normalizeHex = (value: string) => {
  const trimmed = value.trim().toUpperCase()
  if (!trimmed) {
    return null
  }
  const prefixed = trimmed.startsWith('#') ? trimmed : `#${trimmed}`
  if (!/^#([0-9A-F]{6})$/.test(prefixed)) {
    return null
  }
  return prefixed
}

const activeColorPreview = computed(() => normalizeHex(workingColor.value))
const singleColorGuideStyle = computed(() => {
  const hex = activeColorPreview.value

  if (!hex) {
    return {
      color: '#101A34',
      textShadow: '0 1px 0 rgba(255, 255, 255, 0.55)'
    }
  }

  const red = Number.parseInt(hex.slice(1, 3), 16)
  const green = Number.parseInt(hex.slice(3, 5), 16)
  const blue = Number.parseInt(hex.slice(5, 7), 16)
  const luminance = (0.2126 * red + 0.7152 * green + 0.0722 * blue) / 255
  const useDarkText = luminance > 0.62

  return {
    color: useDarkText ? '#101A34' : '#FFFFFF',
    textShadow: useDarkText
      ? '0 1px 0 rgba(255, 255, 255, 0.55)'
      : '0 1px 0 rgba(16, 26, 52, 0.65)'
  }
})

const openBrandHubModal = () => {
  if (!hasTemplateSelection.value) {
    return
  }
  isBrandHubModalOpen.value = true
}

const closeBrandHubModal = () => {
  isBrandHubModalOpen.value = false
}

const onGoogleFontChange = (event: Event) => {
  const target = event.target as HTMLSelectElement | null
  const next = (target?.value || '').trim() || DEFAULT_GOOGLE_FONT
  emit('update-google-font', next)
}

const openStyleHubModal = () => {
  if (!hasTemplateSelection.value) {
    return
  }
  isStyleHubModalOpen.value = true
}

const closeStyleHubModal = () => {
  isStyleHubModalOpen.value = false
}

const openAssetActionModal = () => {
  if (!hasTemplateSelection.value) {
    return
  }
  isAssetActionModalOpen.value = true
}

const closeAssetActionModal = () => {
  isAssetActionModalOpen.value = false
}

const openLogoActionsFromBrandHub = () => {
  openAssetActionModal()
}

const handleAssetAction = (action: 'upload' | 'browse' | 'clear') => {
  if (action === 'upload') {
    emit('open-upload-modal', { type: 'logo' })
  } else if (action === 'browse') {
    emit('open-asset-modal', { type: 'logo' })
  } else {
    emit('clear-asset', { type: 'logo' })
    closeAssetActionModal()
  }
}

const openBackgroundActionModal = (screen: BackgroundScreenKey = 'registration') => {
  if (!hasTemplateSelection.value) {
    return
  }
  activeBackgroundScreen.value = screen
  isBackgroundActionModalOpen.value = true
}

const closeBackgroundActionModal = () => {
  isBackgroundActionModalOpen.value = false
}

const openBackgroundActionsFromBrandHub = (screen: BackgroundScreenKey) => {
  openBackgroundActionModal(screen)
}

const handleBackgroundAction = (action: 'colour' | 'upload' | 'browse' | 'clear') => {
  if (action === 'colour') {
    openColorStudioModal(activeBackgroundScreen.value)
    return
  }

  if (action === 'upload') {
    emit('open-upload-modal', { type: 'background', screen: activeBackgroundScreen.value })
  } else if (action === 'browse') {
    emit('open-asset-modal', { type: 'background', screen: activeBackgroundScreen.value })
  } else {
    emit('clear-asset', { type: 'background', screen: activeBackgroundScreen.value })
    closeBackgroundActionModal()
  }
}

const openColorModal = (key: EditableColorKey, screen: ScreenAppearanceKey = 'registration') => {
  if (!hasTemplateSelection.value) {
    return
  }
  activeStyleScreen.value = screen
  selectedColorKey.value = key
  workingColor.value = activeBrandColors.value[key] || '#FFFFFF'
  isColorModalOpen.value = true
}

const closeColorModal = () => {
  isColorModalOpen.value = false
  selectedColorKey.value = null
}

const saveColorChange = () => {
  if (!selectedColorKey.value) {
    return
  }

  const normalized = normalizeHex(workingColor.value)
  if (!normalized) {
    return
  }

  emit('update-brand-color', {
    screen: activeStyleScreen.value,
    key: selectedColorKey.value,
    value: normalized,
  })

  closeColorModal()
}

const syncColorStudioDrafts = (screen: ScreenAppearanceKey = activeStyleScreen.value) => {
  const colors = screenBrandColorMap.value[screen]
  colorStudioDrafts.value = {
    background: colors.background,
    header: colors.header,
    secondary: colors.secondary,
    buttonText: colors.buttonText
  }
}

const openColorStudioModal = (screen: ScreenAppearanceKey = 'registration') => {
  if (!hasTemplateSelection.value) {
    return
  }

  activeStyleScreen.value = screen
  syncColorStudioDrafts(screen)
  isColorStudioModalOpen.value = true
}

const closeColorStudioModal = () => {
  isColorStudioModalOpen.value = false
}

const openColorStudioFromHub = (screen: ScreenAppearanceKey) => {
  openColorStudioModal(screen)
}

/* TODO IRL2-2342: bulk customisation — apply styles to all pages/buttons at once */
const openButtonStyleModal = (screen: ScreenAppearanceKey = 'registration') => {
  if (!hasTemplateSelection.value) {
    return
  }
  activeStyleScreen.value = screen
  isButtonStyleModalOpen.value = true
}

const closeButtonStyleModal = () => {
  isButtonStyleModalOpen.value = false
}

const openButtonStyleFromHub = (screen: ScreenAppearanceKey) => {
  openButtonStyleModal(screen)
}

const saveButtonStyle = (value: StyledButtonConfig) => {
  emit('update-button-config', {
    screen: activeStyleScreen.value,
    value,
  })
  closeButtonStyleModal()
}

const updateColorStudioDraft = (key: StudioColorKey, value: string) => {
  colorStudioDrafts.value[key] = value
}

const saveColorStudioChanges = () => {
  const nextBackground = normalizeHex(colorStudioDrafts.value.background)
  const nextHeader = normalizeHex(colorStudioDrafts.value.header)
  const nextButton = normalizeHex(colorStudioDrafts.value.secondary)
  const nextButtonText = normalizeHex(colorStudioDrafts.value.buttonText)

  if (nextBackground) {
    emit('update-brand-color', { screen: activeStyleScreen.value, key: 'background', value: nextBackground })
  }
  if (nextHeader) {
    emit('update-brand-color', { screen: activeStyleScreen.value, key: 'header', value: nextHeader })
  }
  if (nextButton) {
    emit('update-brand-color', { screen: activeStyleScreen.value, key: 'secondary', value: nextButton })
  }
  if (nextButtonText) {
    emit('update-brand-color', { screen: activeStyleScreen.value, key: 'buttonText', value: nextButtonText })
  }

  closeColorStudioModal()
}

const applyPalette = (palette: PaletteOption, screen: ScreenAppearanceKey = activeStyleScreen.value) => {
  emit('update-brand-color', { screen, key: 'background', value: palette.background })
  emit('update-brand-color', { screen, key: 'header', value: palette.header })
  emit('update-brand-color', { screen, key: 'secondary', value: palette.button })
  emit('update-brand-color', { screen, key: 'buttonText', value: palette.buttonText })
}

const applyPaletteFromStudio = (palette: PaletteOption) => {
  applyPalette(palette, activeStyleScreen.value)
  colorStudioDrafts.value = {
    background: palette.background,
    header: palette.header,
    secondary: palette.button,
    buttonText: palette.buttonText
  }
  closeColorStudioModal()
}

const getContrastRatio = (hex1: string, hex2: string): number => {
  const luminance = (hex: string) => {
    const r = parseInt(hex.slice(1, 3), 16) / 255
    const g = parseInt(hex.slice(3, 5), 16) / 255
    const b = parseInt(hex.slice(5, 7), 16) / 255
    const toLinear = (c: number) => c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
    return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b)
  }
  const l1 = luminance(hex1)
  const l2 = luminance(hex2)
  const lighter = Math.max(l1, l2)
  const darker = Math.min(l1, l2)
  return (lighter + 0.05) / (darker + 0.05)
}

const contrastWarnings = computed(() => {
  const warnings: string[] = []
  const bg = normalizeHex(colorStudioDrafts.value.background)
  const text = normalizeHex(colorStudioDrafts.value.header)
  const btn = normalizeHex(colorStudioDrafts.value.secondary)
  const btnText = normalizeHex(colorStudioDrafts.value.buttonText)

  if (bg && text) {
    if (bg === text) {
      warnings.push('Base text and background are the same colour — text will be invisible')
    } else if (getContrastRatio(bg, text) < 3) {
      warnings.push('Base text may be hard to read against the background')
    }
  }

  if (btn && btnText) {
    if (btn === btnText) {
      warnings.push('Button text and button are the same colour — text will be invisible')
    } else if (getContrastRatio(btn, btnText) < 3) {
      warnings.push('Button text may be hard to read against the button colour')
    }
  }

  return warnings
})

const singleColorContrastWarning = computed(() => {
  if (!selectedColorKey.value) return null
  const color = normalizeHex(workingColor.value)
  if (!color) return null

  if (selectedColorKey.value === 'header') {
    const bg = normalizeHex(activeBrandColors.value.background)
    if (bg) {
      if (bg === color) return 'This colour is the same as the background — text will be invisible'
      if (getContrastRatio(bg, color) < 3) return 'This colour may be hard to read against the background'
    }
  }

  if (selectedColorKey.value === 'background') {
    const text = normalizeHex(activeBrandColors.value.header)
    if (text) {
      if (text === color) return 'Background is the same as the text colour — text will be invisible'
      if (getContrastRatio(text, color) < 3) return 'Text may be hard to read against this background'
    }
  }

  if (selectedColorKey.value === 'secondary') {
    const btnText = normalizeHex(activeBrandColors.value.buttonText)
    if (btnText) {
      if (btnText === color) return 'Button colour is the same as button text — text will be invisible'
      if (getContrastRatio(btnText, color) < 3) return 'Button text may be hard to read against this colour'
    }
  }

  if (selectedColorKey.value === 'buttonText') {
    const btn = normalizeHex(activeBrandColors.value.secondary)
    if (btn) {
      if (btn === color) return 'Button text is the same as the button colour — text will be invisible'
      if (getContrastRatio(btn, color) < 3) return 'Button text may be hard to read against the button colour'
    }
  }

  return null
})

watch(isAnyModalOpen, (isOpen) => {
  if (isOpen) {
    uiStore.openModal()
  } else {
    uiStore.closeModal()
  }
})

onBeforeUnmount(() => {
  if (isAnyModalOpen.value) {
    uiStore.closeModal()
  }
})
</script>

<style scoped>
.look-feel-section {
  justify-content: flex-start;
  align-items: stretch;
  padding-top: var(--section-padding-block-start);
  --look-feel-form-scale: 1;
  --look-feel-preview-scale: var(--campaign-preview-density-scale, 1);
  --look-feel-modal-scale: 1;
}

@media (min-width: 769px) {
  .look-feel-section {
    justify-content: flex-start;
    align-items: stretch;
    --look-feel-form-scale: calc(var(--desktop-form-viewport-scale, 1) * var(--desktop-display-density-scale, 1));
    --look-feel-preview-scale: calc(
      var(--campaign-preview-density-scale, 1) *
      var(--desktop-preview-scale, 1) *
      var(--desktop-display-density-scale, 1)
    );
    --look-feel-modal-scale: calc(var(--desktop-modal-scale, 1) * var(--desktop-display-density-scale, 1));
    padding-top: var(--section-padding-block-start) !important;
    padding-bottom: max(4.5rem, calc(7rem * var(--desktop-form-viewport-scale, 1)));
  }
}

.look-feel-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: max(0.95rem, calc(var(--section-content-gap) * var(--look-feel-form-scale)));
  width: min(100%, var(--base-layout-section-max-width));
  margin: 0 auto;
}

.section-header {
  text-align: center;
  margin: 0 auto;
  width: min(100%, calc(42rem * var(--look-feel-form-scale)));
}

.section-header h3 {
  font-size: var(--base-layout-section-heading-h3-size);
  color: var(--color-heading);
}

.section-header p {
  color: var(--color-text-muted);
  margin-top: 0.35rem;
}

.palette-option {
  border: 1px solid rgba(148, 163, 184, 0.45);
  border-radius: 0.75rem;
  background: rgba(15, 23, 42, 0.9);
  color: #e2e8f0;
  padding: 0.5rem;
  text-align: center;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
}

.palette-option:hover {
  border-color: rgba(122, 230, 177, 0.9);
}

.palette-name {
  font-size: 0.78rem;
  font-weight: 700;
  display: block;
  margin: 0;
}

.palette-swatches {
  margin-top: 0;
  display: flex;
  gap: 0.3rem;
}

.palette-swatch {
  width: 16px;
  height: 16px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: var(--palette-color);
}

.font-selector-group {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.font-selector-group label {
  color: var(--color-text-muted);
  font-size: 0.86rem;
}

.font-select-input {
  min-width: 180px;
  border-radius: 0.65rem;
  border: 1px solid rgba(148, 163, 184, 0.45);
  background: rgba(15, 23, 42, 0.85);
  color: #e2e8f0;
  padding: 0.42rem 0.55rem;
}

.font-selector-group--studio {
  justify-content: flex-start;
}

.mobile-appearance-controls {
  display: grid;
  grid-template-columns: repeat(2, minmax(210px, 1fr));
  gap: 0.75rem;
  width: min(100%, 620px);
  margin-inline: auto;
  margin-top: 0;
  justify-content: center;
}

.look-feel-section .mobile-control-button {
  width: 100%;
  height: auto;
  min-width: 0;
  max-width: 300px;
  margin-top: 0 !important;
  border: 1px solid var(--vt-c-accent) !important;
  border-radius: var(--base-layout-button-border-radius-lg) !important;
  background: var(--vt-c-accent) !important;
  color: var(--color-background) !important;
  padding: var(--base-layout-button-padding-lg) !important;
  font-size: var(--base-layout-button-font-size-md);
  font-weight: var(--base-layout-button-font-weight);
  letter-spacing: 0.01em;
  cursor: pointer;
  min-height: 42px !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  text-align: center;
  line-height: 1.25;
  box-shadow: none !important;
  white-space: nowrap;
  justify-self: center;
}

.look-feel-section .mobile-control-button:hover,
.look-feel-section .mobile-control-button:focus-visible,
.look-feel-section .mobile-control-button:active {
  background: var(--ui-inverse-cta-bg-hover) !important;
  color: var(--color-background) !important;
}

.mobile-control-button:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

.phone-preview-shell {
  width: 100%;
  display: flex;
  justify-content: center;
  margin-top: calc(var(--campaign-preview-phone-shell-margin-top) * var(--look-feel-form-scale));
  padding-bottom: calc(var(--campaign-preview-phone-shell-padding-bottom) * var(--look-feel-form-scale));
}

.phone-preview {
  --preview-density-scale: var(--look-feel-preview-scale);
  width: var(--campaign-preview-phone-width);
  width: calc(var(--campaign-preview-phone-width) * var(--preview-density-scale));
  max-width: 100%;
  background: #12162a;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: calc(var(--campaign-preview-phone-radius) * var(--preview-density-scale));
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.35);
  padding: calc(var(--campaign-preview-phone-padding) * var(--preview-density-scale));
}

.phone-speaker {
  width: 34%;
  height: calc(var(--campaign-preview-phone-speaker-height) * var(--preview-density-scale));
  margin: 0 auto calc(var(--campaign-preview-phone-speaker-margin-bottom) * var(--preview-density-scale));
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.25);
}

.phone-screen {
  --preview-zone-border: rgba(122, 230, 177, 0.95);
  --preview-zone-border-soft: rgba(122, 230, 177, 0.3);
  border-radius: calc(var(--campaign-preview-phone-screen-radius) * var(--preview-density-scale));
  height: calc(var(--campaign-preview-phone-screen-height) * var(--preview-density-scale));
  border: 1px solid rgba(255, 255, 255, 0.18);
  padding:
    calc(var(--campaign-preview-phone-screen-padding-block) * var(--preview-density-scale))
    calc(var(--campaign-preview-phone-screen-padding-inline) * var(--preview-density-scale));
  display: grid;
  grid-template-rows:
    calc(var(--campaign-preview-phone-logo-height) * var(--preview-density-scale))
    minmax(0, 1fr)
    auto;
  gap: calc(var(--campaign-preview-phone-gap) * var(--preview-density-scale));
  align-items: stretch;
  justify-items: center;
  position: relative;
  overflow: hidden;
}

.background-hit-target {
  position: absolute;
  inset: calc(0.45rem * var(--preview-density-scale));
  width: 100%;
  height: auto;
  margin: 0;
  padding: 0;
  z-index: 1;
  border: none;
  border-radius: calc((var(--campaign-preview-phone-screen-radius) - 0.35rem) * var(--preview-density-scale));
  background: transparent;
  box-shadow: none;
  transform: none;
  cursor: pointer;
}

.background-hit-target:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.background-hit-target:hover {
  transform: none;
  box-shadow: none;
}

.tap-target {
  border: none;
  border-radius: calc(var(--campaign-preview-phone-target-radius) * var(--preview-density-scale));
  background: transparent;
  color: inherit;
  cursor: pointer;
  text-align: left;
  padding:
    calc(var(--campaign-preview-phone-target-padding-block) * var(--preview-density-scale))
    calc(var(--campaign-preview-phone-target-padding-inline) * var(--preview-density-scale));
  position: relative;
  overflow: hidden;
  z-index: 2;
  box-shadow: none;
  backdrop-filter: none;
  transform: none;
}

.marching-ants {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.marching-ants rect {
  fill: none;
  stroke: var(--preview-zone-border);
  stroke-width: 1.6;
  stroke-dasharray: 2.2 2.2;
  stroke-linecap: round;
  vector-effect: non-scaling-stroke;
  animation: marching-ants-offset 1.2s linear infinite;
}

.tap-target:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.tap-target:hover {
  background: transparent;
  box-shadow: none;
  transform: none;
}


.logo-target {
  grid-row: 1;
  align-self: center;
  width: var(--campaign-preview-phone-logo-width);
  height: calc(var(--campaign-preview-phone-logo-height) * var(--preview-density-scale));
  min-height: calc(var(--campaign-preview-phone-logo-height) * var(--preview-density-scale));
  padding: calc(var(--campaign-preview-phone-logo-padding) * var(--preview-density-scale));
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
}

.phone-logo {
  width: auto;
  height: auto;
  max-width: var(--campaign-preview-phone-logo-max-size);
  max-height: var(--campaign-preview-phone-logo-max-size);
  object-fit: contain;
}

.text-target {
  grid-row: 2;
  width: var(--campaign-preview-phone-panel-width);
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: max(0.2rem, calc(0.42rem * var(--preview-density-scale)));
  text-align: center;
  align-self: center;
  justify-self: center;
  /* Total vertical padding is split between the outer box (where the
     marching-ants sit) and an inner ::before pseudo that paints the white
     pane. Keeps the ants OUTSIDE the white pane reliably, with no
     width: auto / negative-inset tricks that misbehave on iOS Safari. */
  padding-top: calc(0.7rem * var(--preview-density-scale));
  padding-bottom: calc(0.7rem * var(--preview-density-scale));
  border-radius: calc(0.65rem * var(--preview-density-scale));
  position: relative;
}

/* White 75% pane — smaller than the text-target, centred inside.
   Marching-ants stay at their natural inset: 0 on the parent and
   therefore appear OUTSIDE this pane. */
.phone-preview .text-target {
  background: transparent !important;
  overflow: visible !important;
}

.phone-preview .text-target::before {
  content: '';
  position: absolute;
  top: calc(0.4rem * var(--preview-density-scale));
  right: calc(0.4rem * var(--preview-density-scale));
  bottom: calc(0.4rem * var(--preview-density-scale));
  left: calc(0.4rem * var(--preview-density-scale));
  background: rgba(255, 255, 255, 0.75);
  border-radius: calc(0.55rem * var(--preview-density-scale));
  pointer-events: none;
  z-index: 0;
}

/* Text content sits above the white pane. */
.phone-preview .text-target > :not(.marching-ants) {
  position: relative;
  z-index: 1;
}

.preview-copy {
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 0;
  max-width: 100%;
  font-size: clamp(
    calc(0.68rem * var(--preview-density-scale)),
    calc((0.64rem + 0.22vw) * var(--preview-density-scale)),
    calc(0.86rem * var(--preview-density-scale))
  );
  font-weight: 600;
  line-height: 1.2;
  width: 100%;
  text-align: center;
  text-wrap: balance;
}

.input-preview {
  border: 1px solid rgba(148, 163, 184, 0.55);
  border-radius: calc(0.55rem * var(--preview-density-scale));
  padding:
    calc(0.38rem * var(--preview-density-scale))
    calc(0.52rem * var(--preview-density-scale));
  min-height: calc(2rem * var(--preview-density-scale));
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: clamp(
    calc(0.64rem * var(--preview-density-scale)),
    calc((0.62rem + 0.18vw) * var(--preview-density-scale)),
    calc(0.8rem * var(--preview-density-scale))
  );
  line-height: 1.1;
  opacity: 0.9;
  width: 100%;
  max-width: 100%;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.button-target {
  grid-row: 3;
  width: var(--campaign-preview-phone-button-width);
  align-self: end;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: calc(var(--campaign-preview-phone-button-shell-padding) * var(--preview-density-scale));
  border-radius: 999px;
  overflow: visible;
}

.preview-submit {
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  width: 100%;
  min-height: calc(var(--campaign-preview-phone-button-min-height) * var(--preview-density-scale));
  border-radius: inherit;
  padding:
    calc(var(--campaign-preview-phone-button-padding-block) * var(--preview-density-scale))
    calc(var(--campaign-preview-phone-button-padding-inline) * var(--preview-density-scale));
  box-sizing: border-box;
  font-weight: 700;
  font-size: clamp(
    calc(0.66rem * var(--preview-density-scale)),
    calc((0.64rem + 0.16vw) * var(--preview-density-scale)),
    calc(0.84rem * var(--preview-density-scale))
  );
}

.button-target .marching-ants {
  inset: 1px;
}

@keyframes marching-ants-offset {
  to {
    stroke-dashoffset: -8.8;
  }
}

.asset-action-modal-overlay {
  --look-feel-modal-scale: 1;
  position: fixed;
  inset: 0;
  z-index: calc(var(--base-layout-modal-z-index) + 30);
  background: rgba(8, 12, 20, 0.72);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: max(0.85rem, calc(1rem * var(--look-feel-modal-scale)));
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

.asset-action-modal {
  width: min(calc(430px * var(--look-feel-modal-scale)), 100%);
  background: #0f172a;
  border: 1px solid rgba(148, 163, 184, 0.3);
  border-radius: max(0.85rem, calc(1rem * var(--look-feel-modal-scale)));
  overflow: hidden;
}

.modal-header {
  padding:
    max(0.8rem, calc(0.95rem * var(--look-feel-modal-scale)))
    max(0.85rem, calc(1rem * var(--look-feel-modal-scale)));
  border-bottom: 1px solid rgba(148, 163, 184, 0.2);
  position: relative;
}

.modal-header h3 {
  margin: 0;
  color: #f8fafc;
  text-align: center;
  padding: 0 2.8rem;
  font-size: clamp(
    calc(1.08rem * var(--look-feel-modal-scale)),
    calc((1rem + 0.32vw) * var(--look-feel-modal-scale)),
    calc(1.32rem * var(--look-feel-modal-scale))
  );
}

.modal-body {
  padding: max(0.82rem, calc(1rem * var(--look-feel-modal-scale)));
}

.modal-body--logo-actions {
  display: grid;
  justify-items: center;
  text-align: center;
}

.modal-body--logo-actions .modal-actions {
  width: 100%;
}

.modal-body p {
  margin: 0 0 max(0.65rem, calc(0.8rem * var(--look-feel-modal-scale)));
  color: #cbd5e1;
  font-size: calc(0.96rem * var(--look-feel-modal-scale));
}

.modal-actions {
  display: grid;
  gap: max(0.5rem, calc(0.6rem * var(--look-feel-modal-scale)));
  margin-top: max(0.55rem, calc(0.75rem * var(--look-feel-modal-scale)));
}

.asset-action-modal--studio {
  width: min(calc(620px * var(--look-feel-modal-scale)), 100%);
  max-height: min(84vh, calc(760px * var(--look-feel-modal-scale)));
  display: flex;
  flex-direction: column;
  background: var(--ui-inverse-surface) !important;
  border: none !important;
  box-shadow: none !important;
  --ui-inverse-text: #ffffff;
}

.asset-action-modal--studio .modal-header,
.asset-action-modal--studio .modal-body {
  background: var(--ui-inverse-surface) !important;
}

.asset-action-modal--studio .modal-header {
  border-bottom: none !important;
}

.asset-action-modal-overlay .asset-action-modal.asset-action-modal--studio h3,
.asset-action-modal-overlay .asset-action-modal.asset-action-modal--studio p,
.asset-action-modal-overlay .asset-action-modal.asset-action-modal--studio label,
.asset-action-modal-overlay .asset-action-modal.asset-action-modal--studio span,
.asset-action-modal-overlay .asset-action-modal.asset-action-modal--studio li {
  color: #ffffff !important;
}

.asset-action-modal--studio .modal-body {
  overflow-y: auto;
  min-height: 0;
  max-height: calc(84vh - calc(4.4rem * var(--look-feel-modal-scale)));
}

.asset-action-modal-overlay .asset-action-modal.asset-action-modal--studio .action-button {
  border: 1px solid var(--vt-c-accent) !important;
  border-radius: max(0.6rem, calc(0.7rem * var(--look-feel-modal-scale)));
  background: var(--vt-c-accent) !important;
  color: var(--color-background) !important;
  margin-top: 0;
  padding: calc(0.65rem * var(--look-feel-modal-scale)) calc(0.8rem * var(--look-feel-modal-scale));
  font-size: calc(0.94rem * var(--look-feel-modal-scale));
  font-weight: 600;
  cursor: pointer;
}

.asset-action-modal-overlay .asset-action-modal.asset-action-modal--studio .action-button:hover {
  border-color: var(--ui-inverse-cta-bg-hover) !important;
  background: var(--ui-inverse-cta-bg-hover) !important;
  color: var(--color-background) !important;
}

.asset-action-modal-overlay .asset-action-modal.asset-action-modal--logo {
  background: var(--ui-inverse-surface) !important;
  border: none !important;
  box-shadow: none !important;
  --ui-inverse-text: #ffffff;
}

.asset-action-modal--logo .modal-header,
.asset-action-modal--logo .modal-body {
  background: var(--ui-inverse-surface) !important;
}

.asset-action-modal--logo .modal-header {
  border-bottom: none !important;
}

.asset-action-modal-overlay .asset-action-modal.asset-action-modal--logo h3,
.asset-action-modal-overlay .asset-action-modal.asset-action-modal--logo p,
.asset-action-modal-overlay .asset-action-modal.asset-action-modal--logo label,
.asset-action-modal-overlay .asset-action-modal.asset-action-modal--logo span,
.asset-action-modal-overlay .asset-action-modal.asset-action-modal--logo li {
  color: #ffffff !important;
}

.asset-action-modal-overlay .asset-action-modal.asset-action-modal--logo .action-button {
  border: 1px solid var(--vt-c-accent) !important;
  border-radius: max(0.6rem, calc(0.7rem * var(--look-feel-modal-scale)));
  background: var(--vt-c-accent) !important;
  color: var(--color-background) !important;
  margin-top: 0;
  padding: calc(0.65rem * var(--look-feel-modal-scale)) calc(0.8rem * var(--look-feel-modal-scale));
  font-size: calc(0.94rem * var(--look-feel-modal-scale));
  font-weight: 600;
  cursor: pointer;
}

.asset-action-modal-overlay .asset-action-modal.asset-action-modal--logo .action-button:hover {
  border-color: var(--ui-inverse-cta-bg-hover) !important;
  background: var(--ui-inverse-cta-bg-hover) !important;
  color: var(--color-background) !important;
}

.asset-action-modal-overlay .asset-action-modal.asset-action-modal--background {
  background: var(--ui-inverse-surface) !important;
  border: none !important;
  box-shadow: none !important;
  --ui-inverse-text: #ffffff;
}

.asset-action-modal--background .modal-header,
.asset-action-modal--background .modal-body {
  background: var(--ui-inverse-surface) !important;
}

.asset-action-modal--background .modal-header {
  border-bottom: none !important;
}

.asset-action-modal-overlay .asset-action-modal.asset-action-modal--background h3,
.asset-action-modal-overlay .asset-action-modal.asset-action-modal--background p,
.asset-action-modal-overlay .asset-action-modal.asset-action-modal--background label,
.asset-action-modal-overlay .asset-action-modal.asset-action-modal--background span,
.asset-action-modal-overlay .asset-action-modal.asset-action-modal--background li {
  color: #ffffff !important;
}

.asset-action-modal-overlay .asset-action-modal.asset-action-modal--background .action-button {
  border: 1px solid var(--vt-c-accent) !important;
  border-radius: max(0.6rem, calc(0.7rem * var(--look-feel-modal-scale)));
  background: var(--vt-c-accent) !important;
  color: var(--color-background) !important;
  margin-top: 0;
  padding: calc(0.65rem * var(--look-feel-modal-scale)) calc(0.8rem * var(--look-feel-modal-scale));
  font-size: calc(0.94rem * var(--look-feel-modal-scale));
  font-weight: 600;
  cursor: pointer;
}

.asset-action-modal-overlay .asset-action-modal.asset-action-modal--background .action-button:hover {
  border-color: var(--ui-inverse-cta-bg-hover) !important;
  background: var(--ui-inverse-cta-bg-hover) !important;
  color: var(--color-background) !important;
}

.asset-action-modal-overlay .asset-action-modal.asset-action-modal--hub {
  width: min(calc(560px * var(--look-feel-modal-scale)), 100%);
  max-height: min(84vh, calc(760px * var(--look-feel-modal-scale)));
  display: flex;
  flex-direction: column;
  background: var(--ui-inverse-surface) !important;
  --ui-inverse-text: #ffffff;
}

.asset-action-modal--hub .modal-body {
  overflow-y: auto;
  min-height: 0;
  max-height: calc(84vh - calc(4.4rem * var(--look-feel-modal-scale)));
}

.hub-block {
  display: grid;
  gap: 0;
  margin-bottom: max(0.6rem, calc(0.72rem * var(--look-feel-modal-scale)));
}

.hub-block:last-child {
  margin-bottom: 0;
}

.asset-action-modal-overlay .asset-action-modal.asset-action-modal--hub .hub-block-copy {
  margin: 0;
  color: #ffffff !important;
}

.screen-control-list {
  display: grid;
  gap: max(0.55rem, calc(0.65rem * var(--look-feel-modal-scale)));
}

.screen-control-card {
  display: grid;
  gap: max(0.58rem, calc(0.7rem * var(--look-feel-modal-scale)));
  padding: max(0.68rem, calc(0.8rem * var(--look-feel-modal-scale)));
  border-radius: max(0.72rem, calc(0.85rem * var(--look-feel-modal-scale)));
  border: 1px solid rgba(148, 163, 184, 0.24);
  background: rgba(15, 23, 42, 0.72);
}

.screen-control-copy {
  display: grid;
  gap: 0.2rem;
}

.screen-control-title {
  color: #f8fafc;
  font-weight: 700;
}

.asset-action-modal-overlay .asset-action-modal.asset-action-modal--hub .screen-control-description {
  color: #ffffff !important;
  font-size: calc(0.9rem * var(--look-feel-modal-scale));
}

.screen-control-actions {
  display: grid;
  gap: max(0.45rem, calc(0.55rem * var(--look-feel-modal-scale)));
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.asset-action-modal-overlay .asset-action-modal.asset-action-modal--hub .action-button {
  border: 1px solid var(--vt-c-accent) !important;
  border-radius: max(0.6rem, calc(0.7rem * var(--look-feel-modal-scale)));
  background: var(--vt-c-accent) !important;
  color: var(--color-background) !important;
  margin-top: 0;
  padding: calc(0.65rem * var(--look-feel-modal-scale)) calc(0.8rem * var(--look-feel-modal-scale));
  font-size: calc(0.94rem * var(--look-feel-modal-scale));
  font-weight: 600;
  cursor: pointer;
}

.asset-action-modal-overlay .asset-action-modal.asset-action-modal--hub .action-button:hover {
  border-color: var(--ui-inverse-cta-bg-hover) !important;
  background: var(--ui-inverse-cta-bg-hover) !important;
  color: var(--color-background) !important;
}

.action-button--danger {
  color: #fda4af;
  border-color: rgba(244, 63, 94, 0.5);
}

.asset-action-modal--single-color {
  width: min(calc(480px * var(--look-feel-modal-scale)), 100%);
  max-height: min(84vh, calc(720px * var(--look-feel-modal-scale)));
  display: flex;
  flex-direction: column;
  background: var(--ui-inverse-surface) !important;
  border: none !important;
  box-shadow: none !important;
  --ui-inverse-text: #ffffff;
}

.asset-action-modal--single-color .modal-body {
  overflow-y: auto;
  min-height: 0;
}

.asset-action-modal--single-color .modal-actions {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-top: 0.2rem;
}

.asset-action-modal--single-color .modal-header,
.asset-action-modal--single-color .modal-body {
  background: var(--ui-inverse-surface) !important;
}

.asset-action-modal--single-color .modal-header {
  border-bottom: none !important;
}

.asset-action-modal-overlay .asset-action-modal.asset-action-modal--single-color h3,
.asset-action-modal-overlay .asset-action-modal.asset-action-modal--single-color p,
.asset-action-modal-overlay .asset-action-modal.asset-action-modal--single-color label,
.asset-action-modal-overlay .asset-action-modal.asset-action-modal--single-color span,
.asset-action-modal-overlay .asset-action-modal.asset-action-modal--single-color li {
  color: #ffffff !important;
}

.asset-action-modal-overlay .asset-action-modal.asset-action-modal--single-color .action-button {
  border: 1px solid var(--vt-c-accent) !important;
  border-radius: max(0.6rem, calc(0.7rem * var(--look-feel-modal-scale)));
  background: var(--vt-c-accent) !important;
  color: var(--color-background) !important;
  margin-top: 0;
  padding: calc(0.65rem * var(--look-feel-modal-scale)) calc(0.8rem * var(--look-feel-modal-scale));
  font-size: calc(0.94rem * var(--look-feel-modal-scale));
  font-weight: 600;
  cursor: pointer;
}

.asset-action-modal-overlay .asset-action-modal.asset-action-modal--single-color .action-button:hover {
  border-color: var(--ui-inverse-cta-bg-hover) !important;
  background: var(--ui-inverse-cta-bg-hover) !important;
  color: var(--color-background) !important;
}

.modal-body--single-color {
  display: grid;
  gap: max(0.55rem, calc(0.65rem * var(--look-feel-modal-scale)));
  justify-items: center;
  text-align: center;
}

.modal-body--single-color .single-color-controls {
  width: 100%;
}

.single-color-description {
  margin: 0;
  color: #ffffff;
}

.single-color-controls {
  display: grid;
  grid-template-columns: calc(84px * var(--look-feel-modal-scale)) 1fr;
  gap: max(0.45rem, calc(0.55rem * var(--look-feel-modal-scale)));
  align-items: stretch;
}

.single-color-wheel-wrap {
  position: relative;
  width: 100%;
  min-height: calc(84px * var(--look-feel-modal-scale));
}

.single-color-wheel-guide {
  position: absolute;
  inset: 0;
  z-index: 4;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 calc(0.25rem * var(--look-feel-modal-scale));
  font-size: calc(0.68rem * var(--look-feel-modal-scale));
  line-height: 1.1;
  font-weight: 800;
  color: #ffffff;
  text-align: center;
  pointer-events: none;
  user-select: none;
  text-transform: uppercase;
}

.single-color-wheel {
  position: relative;
  z-index: 1;
  width: 100%;
  min-height: calc(84px * var(--look-feel-modal-scale));
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(15, 23, 42, 0.32);
  padding: 0;
  cursor: pointer;
  border-radius: max(0.6rem, calc(0.7rem * var(--look-feel-modal-scale)));
}

.single-color-wheel::-webkit-color-swatch-wrapper {
  padding: 0;
}

.single-color-wheel::-webkit-color-swatch,
.single-color-wheel::-moz-color-swatch {
  border: none;
  border-radius: max(0.6rem, calc(0.7rem * var(--look-feel-modal-scale)));
}

.color-hex-input--single {
  border-radius: max(0.68rem, calc(0.8rem * var(--look-feel-modal-scale)));
  min-height: calc(48px * var(--look-feel-modal-scale));
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  background: rgba(15, 23, 42, 0.85);
  color: #ffffff;
}

.color-controls {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: max(0.5rem, calc(0.6rem * var(--look-feel-modal-scale)));
  align-items: center;
}

.color-picker-input {
  width: calc(44px * var(--look-feel-modal-scale));
  height: calc(44px * var(--look-feel-modal-scale));
  border: none;
  background: transparent;
  padding: 0;
}

.color-hex-input {
  border-radius: max(0.55rem, calc(0.65rem * var(--look-feel-modal-scale)));
  border: 1px solid rgba(148, 163, 184, 0.45);
  background: rgba(15, 23, 42, 0.85);
  color: #e2e8f0;
  padding: calc(0.5rem * var(--look-feel-modal-scale)) calc(0.6rem * var(--look-feel-modal-scale));
}

.color-preset-grid {
  display: grid;
  grid-template-columns: repeat(8, minmax(0, 1fr));
  gap: max(0.3rem, calc(0.35rem * var(--look-feel-modal-scale)));
  margin-top: max(0.55rem, calc(0.7rem * var(--look-feel-modal-scale)));
}

.color-preset-grid--single {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: nowrap;
  gap: max(0.4rem, calc(0.5rem * var(--look-feel-modal-scale)));
  margin-top: 0;
}

.color-preset {
  width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  background: var(--preset-color);
  cursor: pointer;
}

.color-preset--single {
  flex: 0 0 calc(2.2rem * var(--look-feel-modal-scale));
  width: calc(2.2rem * var(--look-feel-modal-scale));
  height: calc(2.2rem * var(--look-feel-modal-scale));
  aspect-ratio: 1 / 1;
  border-radius: max(0.6rem, calc(0.7rem * var(--look-feel-modal-scale)));
  border: 1px solid rgba(255, 255, 255, 0.22);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.3);
}

.color-preset--active {
  outline: 2px solid var(--vt-c-accent);
  outline-offset: 1px;
}

@media (min-width: 769px) {
  .asset-action-modal-overlay {
    --look-feel-modal-scale: calc(var(--desktop-modal-scale, 1) * var(--desktop-display-density-scale, 1));
  }

  /* Overflow-safe vertical centering on desktop: auto margins centre the modal
     when it fits, and collapse to top-aligned-and-scrollable when the content is
     taller than the viewport (e.g. short laptop screens). This supersedes the
     align-items: flex-start override in the short-viewport block below. */
  .asset-action-modal {
    margin-block: auto;
  }

  .single-color-controls {
    grid-template-columns: 1fr;
    justify-items: center;
    gap: 0.45rem;
    width: min(11.5rem, 100%);
    margin-inline: auto;
  }

  .single-color-wheel-wrap {
    width: 5.25rem;
  }

  .color-hex-input--single {
    width: 100%;
    min-height: 2.45rem;
    padding: 0.35rem 0.55rem;
    font-size: 0.84rem;
    text-align: center;
  }
}

.studio-block {
  margin-bottom: max(0.85rem, calc(1rem * var(--look-feel-modal-scale)));
}

.studio-block-title {
  margin: 0 0 max(0.45rem, calc(0.55rem * var(--look-feel-modal-scale)));
  color: #f8fafc;
  font-weight: 800;
}

.asset-action-modal-overlay .asset-action-modal.asset-action-modal--hub .studio-block-title {
  color: #ffffff !important;
  font-weight: 800 !important;
}

.studio-palette-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(152px, 1fr));
  gap: max(0.42rem, calc(0.5rem * var(--look-feel-modal-scale)));
}

.palette-option--studio {
  min-height: calc(88px * var(--look-feel-modal-scale));
}

.studio-color-list {
  display: grid;
  gap: max(0.55rem, calc(0.65rem * var(--look-feel-modal-scale)));
}

.studio-color-item {
  border: 1px solid rgba(148, 163, 184, 0.32);
  border-radius: max(0.62rem, calc(0.75rem * var(--look-feel-modal-scale)));
  padding: max(0.45rem, calc(0.55rem * var(--look-feel-modal-scale)));
  background: rgba(15, 23, 42, 0.6);
}

.studio-color-item-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: max(0.45rem, calc(0.55rem * var(--look-feel-modal-scale)));
  margin-bottom: max(0.38rem, calc(0.45rem * var(--look-feel-modal-scale)));
  color: #f8fafc;
  font-weight: 600;
}

.studio-color-item-description {
  font-size: calc(0.75rem * var(--look-feel-modal-scale));
  color: rgba(203, 213, 225, 0.8);
  text-align: right;
}

@media (max-width: 768px) {
  .asset-action-modal-overlay {
    align-items: flex-start;
    padding: var(--mobile-modal-overlay-top-padding) var(--mobile-modal-overlay-side-padding) var(--mobile-modal-overlay-bottom-padding);
  }

  .look-feel-section .mobile-appearance-controls {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    width: min(100%, 360px);
    gap: 0.5rem;
  }

  .look-feel-section .mobile-control-button {
    max-width: none;
    padding: 0.72rem 0.7rem !important;
    font-size: 0.82rem !important;
    min-height: 42px !important;
  }

  .phone-logo {
    max-height: 52px;
  }

  .text-target {
    min-height: 0;
    gap: 0.24rem;
  }

  .preview-copy {
    font-size: 0.76rem;
  }

  .input-preview {
    margin-top: 0;
    font-size: 0.72rem;
  }

  .preview-submit {
    font-size: 0.82rem;
  }

  .color-preset-grid {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }

  .color-preset-grid--single {
    display: flex;
    justify-content: center;
  }

  .asset-action-modal--single-color {
    max-height: min(var(--mobile-modal-max-height), 88vh);
  }

  .asset-action-modal--single-color .modal-body {
    max-height: calc(min(var(--mobile-modal-max-height), 88vh) - 4.4rem);
  }

  .asset-action-modal--single-color .modal-actions {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .single-color-controls {
    grid-template-columns: 78px 1fr;
  }

  .asset-action-modal--studio {
    max-height: min(var(--mobile-modal-max-height), 88vh);
  }

  .asset-action-modal--studio .modal-body {
    max-height: calc(min(var(--mobile-modal-max-height), 88vh) - 4.4rem);
  }

  .asset-action-modal--hub {
    max-height: min(var(--mobile-modal-max-height), 88vh);
  }

  .asset-action-modal--hub .modal-body {
    max-height: calc(min(var(--mobile-modal-max-height), 88vh) - 4.4rem);
  }

  .studio-palette-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .studio-color-item-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .studio-color-item-description {
    text-align: left;
  }
}

@media (min-width: 769px) and (max-height: 900px) {
  /* Short desktops / MacBooks — trim preview scale a touch and give the
     section more vertical breathing room between header, buttons, and phone. */
  .look-feel-section {
    --look-feel-preview-scale: calc(
      var(--campaign-preview-density-scale, 1) *
      var(--desktop-preview-scale, 1) *
      var(--desktop-display-density-scale, 1) *
      0.92
    );
  }

  .look-feel-content {
    gap: max(1.25rem, calc(var(--section-content-gap) * var(--look-feel-form-scale) * 1.15));
  }

  .section-header h3 {
    font-size: clamp(1.35rem, 2.2vw, 1.85rem);
  }

  .phone-preview-shell {
    margin-top: calc(var(--campaign-preview-phone-shell-margin-top) * var(--look-feel-form-scale) * 1.2);
  }

  .asset-action-modal-overlay {
    align-items: flex-start;
    padding-top: max(1rem, calc(1.25rem * var(--look-feel-modal-scale)));
    padding-bottom: max(1rem, calc(1.25rem * var(--look-feel-modal-scale)));
  }

  .look-feel-section .mobile-appearance-controls {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    width: min(100%, 360px);
    gap: 0.5rem;
  }

  .look-feel-section .mobile-control-button {
    max-width: none;
    padding: 0.72rem 0.7rem !important;
    font-size: 0.82rem !important;
    border-radius: var(--base-layout-button-border-radius-lg) !important;
  }
}

.contrast-warning {
  display: flex;
  align-items: flex-start;
  gap: 0.45rem;
  padding: 0.55rem 0.7rem;
  margin-top: 0.5rem;
  border-radius: 0.6rem;
  background: rgba(127, 29, 29, 0.34);
  border: 2px solid #ff4d4f;
  color: #ffffff;
  font-size: 0.82rem;
  line-height: 1.35;
  font-weight: 700;
}

.contrast-warning-icon {
  flex-shrink: 0;
  font-size: 1rem;
  color: #ff6b6d;
}

.contrast-warning ul {
  margin: 0;
  padding: 0 0 0 1rem;
  list-style: disc;
}

.contrast-warning li {
  margin-bottom: 0.15rem;
  color: #ffffff;
  font-weight: 700;
}

@media (max-width: 420px) {
  /* Small iPhones — shrink the phone preview so it doesn't collide with the
     fixed footer, and trim the header text to match. Footer sizing is
     intentionally left alone. */
  .look-feel-section {
    --look-feel-preview-scale: calc(var(--campaign-preview-density-scale, 1) * 0.93);
  }

  .phone-preview {
    width: clamp(155px, 50vw, 210px);
  }

  .phone-preview-shell {
    margin-top: calc(var(--campaign-preview-phone-shell-margin-top) * 0.65);
    padding-bottom: calc(var(--campaign-preview-phone-shell-padding-bottom) * 0.65);
  }

  .section-header {
    width: min(100%, 18rem);
  }

  .section-header h3 {
    font-size: clamp(1.05rem, 4.8vw, 1.28rem);
    line-height: 1.15;
  }

  .section-header p {
    margin-top: 0.22rem;
    font-size: 0.78rem;
    line-height: 1.3;
  }

  .look-feel-section .mobile-appearance-controls {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    width: min(100%, 360px);
    gap: 0.45rem;
  }

  .look-feel-section .mobile-control-button {
    max-width: none;
    padding: 0.68rem 0.6rem !important;
    font-size: 0.78rem !important;
    border-radius: var(--base-layout-button-border-radius-lg) !important;
  }

  .single-color-controls {
    grid-template-columns: 1fr;
  }

  .single-color-wheel {
    min-height: 72px;
  }

  .single-color-wheel-wrap {
    min-height: 72px;
  }

  .phone-logo {
    max-height: 44px;
  }

  .text-target {
    min-height: 0;
    gap: 0.16rem;
  }

  .preview-copy {
    font-size: 0.62rem;
    line-height: 1.15;
  }

  .input-preview {
    margin-top: 0;
    min-height: 1.45rem;
    font-size: 0.58rem;
    border-radius: 0.42rem;
  }

  .button-target {
    width: min(90%, 176px);
  }

  .preview-submit {
    font-size: 0.64rem;
    line-height: 1.05;
  }
}

/* Larger small phones (Samsung, Pixel, larger iPhones) — bump preview scale
   back up. iPhone SE range below 390 stays at the tighter 0.93 scale. */
@media (min-width: 390px) and (max-width: 420px) {
  .look-feel-section {
    --look-feel-preview-scale: calc(var(--campaign-preview-density-scale, 1) * 0.98);
  }

  .phone-logo {
    max-height: 52px;
  }

  .preview-copy {
    font-size: 0.7rem;
  }

  .input-preview {
    font-size: 0.66rem;
  }

  .preview-submit {
    font-size: 0.72rem;
  }
}
</style>
